"use client";

/**
 * Kitle kaynaklı pazar fiyatı bildirimi (2026-10-02). Gönderim: POST /api/v1/bazaar-reports (multipart:
 * `payload` JSON + isteğe bağlı `photo`). Fotoğraf sunucuda yüzleri bulanıklaştırılarak, herkese kapalı saklanır,
 * 30 günde silinir; yayına yalnız sayılar çıkar ve editör onayından sonra.
 */
import Link from "next/link";
import { useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import BazaarItemRows, { type ItemRow } from "./BazaarItemRows";

const todayTr = () => new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 10);
const minDate = () => new Date(Date.now() + 3 * 3600_000 - 14 * 86_400_000).toISOString().slice(0, 10);
const LABEL = "block space-y-2 text-label font-semibold text-(--color-foreground)";
const FIELD = "h-12 w-full rounded-xl border border-(--color-border) bg-(--color-surface) px-3 font-normal";

export default function BazaarReportForm({ cities, products }: { cities: string[]; products: string[] }) {
  const id = useId();
  const [items, setItems] = useState<ItemRow[]>([{ productLabel: "", price: "", unit: "kg" }]);
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const rows = items.filter((r) => r.productLabel.trim() && Number(r.price.replace(",", ".")) > 0);
    if (!rows.length) { setStatus("error"); setMessage("En az bir ürün ve fiyat girin."); return; }
    const payload = {
      city: f.get("city"), district: f.get("district"), bazaarName: f.get("bazaarName"), observedDate: f.get("observedDate"),
      items: rows.map((r) => ({ productLabel: r.productLabel.trim(), price: Number(r.price.replace(",", ".")), unit: r.unit })),
      contactEmail: f.get("contactEmail"), consentKvkk: f.get("consentKvkk") === "on", consentUse: f.get("consentUse") === "on",
      website: f.get("website") || undefined,
    };
    const body = new FormData();
    body.append("payload", JSON.stringify(payload));
    const photo = f.get("photo");
    if (photo instanceof File && photo.size > 0) body.append("photo", photo);
    setStatus("loading");
    try {
      const res = await fetch("/api/v1/bazaar-reports", { method: "POST", body });
      if (res.ok) { setStatus("ok"); return; }
      const code = ((await res.json().catch(() => ({}))) as { error?: { code?: string } }).error?.code;
      setMessage(code === "too_many_reports" ? "Bir saatte en fazla 5 bildirim gönderebilirsiniz." : code === "date_out_of_range" ? "Tarih son 14 gün içinde olmalı." : code === "invalid_photo" ? "Fotoğraf okunamadı; JPEG ya da PNG deneyin." : "Bildirim gönderilemedi; alanları kontrol edip yeniden deneyin.");
      setStatus("error");
    } catch { setStatus("error"); setMessage("Bağlantı hatası; yeniden deneyin."); }
  }

  if (status === "ok") return (
    <div role="status" className="rounded-xl border border-(--color-border-soft) bg-(--color-surface) p-6">
      <h2 className="type-h3">Teşekkürler, bildiriminiz alındı.</h2>
      <p className="mt-2 text-label text-(--color-muted)">Editör inceledikten sonra fiyatlar pazar sütununa girer. Aynı pazar için ikinci bir bağımsız bildirim gelirse kayıt &quot;doğrulandı&quot; olarak işaretlenir.</p>
      <Button className="mt-4" onClick={() => { setItems([{ productLabel: "", price: "", unit: "kg" }]); setStatus("idle"); }}>Yeni bildirim</Button>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" noValidate={false}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className={LABEL}>İl *
          <select name="city" required defaultValue="" className={FIELD}><option value="" disabled>İl seçin</option>{cities.map((c) => <option key={c}>{c}</option>)}</select>
        </label>
        <label className={LABEL}>İlçe<Input name="district" maxLength={64} placeholder="ör. Keçiören" /></label>
        <label className={LABEL}>Pazar adı *<Input name="bazaarName" required minLength={2} maxLength={128} placeholder="ör. Etlik Çarşamba Pazarı" /></label>
        <label className={LABEL}>Tarih *<input name="observedDate" type="date" required min={minDate()} max={todayTr()} defaultValue={todayTr()} className={FIELD} /></label>
      </div>
      <BazaarItemRows rows={items} onChange={setItems} products={products} />
      <label className={LABEL}>Fotoğraf (isteğe bağlı)
        <input name="photo" type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="block w-full text-label" />
        <span className="block text-caption font-normal text-(--color-muted)">Fiyat etiketinin okunduğu bir tezgâh fotoğrafı yeterli. Yüzler otomatik bulanıklaştırılır; fotoğraf yayımlanmaz.</span>
      </label>
      <label className={LABEL}>E-posta (isteğe bağlı)<Input name="contactEmail" type="email" maxLength={190} placeholder="Yalnız soru sormamız gerekirse" /></label>
      <div aria-hidden="true" className="hidden"><label htmlFor={`${id}-w`}>Web sitesi</label><input id={`${id}-w`} name="website" tabIndex={-1} autoComplete="off" /></div>
      <label className="flex items-start gap-3 text-label leading-relaxed text-(--color-muted)">
        <input type="checkbox" name="consentKvkk" required className="mt-1 h-4 w-4 shrink-0 accent-(--color-brand)" />
        <span>Bu sayfadaki aydınlatma metnini ve <Link href="/kvkk" className="font-semibold text-(--color-brand) underline">KVKK Aydınlatma Metni</Link>&apos;ni okudum.</span>
      </label>
      <label className="flex items-start gap-3 text-label leading-relaxed text-(--color-muted)">
        <input type="checkbox" name="consentUse" required className="mt-1 h-4 w-4 shrink-0 accent-(--color-brand)" />
        <span>Bildirdiğim fiyatların kaynak gösterilmeden, sayı olarak yayımlanmasına ve fotoğrafın yalnız doğrulama için 30 gün saklanmasına izin veriyorum.</span>
      </label>
      {status === "error" ? <p role="alert" className="rounded-xl bg-(--color-danger-bg) p-3 text-label text-(--color-danger)">{message}</p> : null}
      <Button type="submit" disabled={status === "loading"}>{status === "loading" ? "Gönderiliyor…" : "Bildirimi gönder"}</Button>
    </form>
  );
}
